/**
 * Demandes d'accès à un projet — lot NAV 0.5.x (registre n° 11 ; V620, ARB-SEC-NAV-02).
 *
 * Module séparé de `api` (client.ts), comme `agregatsApi` : la surface caractérisée du client n'est
 * pas modifiée. Formes lues dans `ProjectAccessRequestService` (passerelle).
 */
import { request } from './http'

export type StatutDemandeAcces = 'pending' | 'approved' | 'rejected' | 'cancelled'

export interface DemandeAcces {
  id: number
  workspace_id: string
  workspace_name: string
  requester_user_id: string
  requester_username: string
  requester_name: string
  message: string | null
  status: StatutDemandeAcces
  granted_role: 'viewer' | 'contributor' | null
  decided_by: string | null
  decision_reason: string | null
  decided_at: string | null
  created_at: string
}

export type DecisionAcces =
  | { decision: 'approve'; role: 'viewer' | 'contributor'; reason?: string }
  | { decision: 'reject'; reason?: string }

/** Réponse de `GET /workspaces/{id}/existence` (D-17) : rien d'autre que l'identifiant demandé. */
export interface ExistenceProjet {
  workspace_id: string
  exists: true
}

const enc = encodeURIComponent

export const demandesAccesApi = {
  /** Demander l'accès à un projet dont on n'est pas membre (201). */
  demander: (workspaceId: string, message?: string) =>
    request<DemandeAcces>(`/workspaces/${enc(workspaceId)}/access-requests`, {
      method: 'POST', body: JSON.stringify(message ? { message } : {}),
    }),
  /** Demandes d'UN projet, pour qui peut les décider (propriétaire, administrateur de la plateforme). */
  duProjet: (workspaceId: string, statut?: StatutDemandeAcces) =>
    request<DemandeAcces[]>(`/workspaces/${enc(workspaceId)}/access-requests${statut ? `?status=${enc(statut)}` : ''}`),
  decider: (workspaceId: string, requestId: number, decision: DecisionAcces) =>
    request<DemandeAcces>(`/workspaces/${enc(workspaceId)}/access-requests/${requestId}/decision`, {
      method: 'POST', body: JSON.stringify(decision),
    }),
  /**
   * Le projet existe-t-il dans MON organisation (D-17) ? 200 s'il y existe, membre ou non ; 404, le même, s'il
   * n'existe pas ou appartient à une autre organisation. Ni son nom ni son état ne sont rendus.
   */
  existence: (workspaceId: string) => request<ExistenceProjet>(`/workspaces/${enc(workspaceId)}/existence`),
  /** Mes demandes, tous projets et tous statuts. */
  mesDemandes: () => request<DemandeAcces[]>('/users/me/access-requests'),
  /** Les demandes EN ATTENTE que je peux décider. */
  aDecider: () => request<DemandeAcces[]>('/users/me/access-requests/to-decide'),
  annuler: (requestId: number) =>
    request<DemandeAcces>(`/users/me/access-requests/${requestId}/cancel`, { method: 'POST', body: '{}' }),
}
